import Vue from 'vue';
import store from '@/store/store';
import Router from 'vue-router';
import * as Routes from './routes';
import * as Roles from '@/constants/user-types';
import * as AccessListMutations from '@/store/modules/access-list/mutations';
import { getStartPage } from '@/store/modules/login/login.module';
import { RecaptchaVisibilityManager } from '@/router/recaptcha';
import { resetPage } from '@/store/modules/page/reset';
import { UserData } from '@/common/auth/user-data';
import { cancelToken } from '@/common/utils/http';

Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: '/schools/',
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes: [
        {
            path: '/login',
            name: Routes.LOGIN,
            component: () => import(/* webpackChunkName: "auth" */ '@/components/no-auth/Login'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/sign-up',
            name: Routes.SIGNUP_PROMO,
            component: () => import(/* webpackChunkName: "auth" */ '@/components/no-auth/SignUpPromo'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/sign-up/:type',
            name: Routes.SIGNUP,
            component: () => import(/* webpackChunkName: "auth" */ '@/components/no-auth/SignUp'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/confirm',
            name: Routes.REGISTRATION_CONFIRM,
            component: () => import(/* webpackChunkName: "auth" */ '@/components/no-auth/RegistrationConfirm'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/suspended',
            name: Routes.PAGE_ACCOUNT_SUSPENDED,
            component: () => import(/* webpackChunkName: "auth" */ '@/components/no-auth/Suspended'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/invite',
            name: Routes.INVITATION_CONFIRM,
            component: () => import(/* webpackChunkName: "auth" */ '@/components/no-auth/UserInvite'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/forgot-password',
            name: Routes.FORGOT_PASSWORD,
            component: () => import(/* webpackChunkName: "auth" */ '@/components/no-auth/ForgotPassword'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/public/classes/calendar',
            name: Routes.PUBLIC_CLASS_CALENDAR,
            component: () => import(/* webpackChunkName: "widgets" */ '@/components/main/widgets/PublicClassCalendar'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/public/classes/list',
            name: Routes.PUBLIC_CLASS_LIST,
            component: () => import(/* webpackChunkName: "widgets" */ '@/components/main/widgets/PublicClassList'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/public/camps/calendar',
            name: Routes.PUBLIC_CAMP_CALENDAR,
            component: () => import(/* webpackChunkName: "widgets" */ '@/components/main/widgets/PublicCampCalendar'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/public/camps/list',
            name: Routes.PUBLIC_CAMP_LIST,
            component: () => import(/* webpackChunkName: "widgets" */ '@/components/main/widgets/PublicCampList'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/public/after-care/list',
            name: Routes.PUBLIC_AFTERCARE_LIST,
            component: () => import(/* webpackChunkName: "widgets" */ '@/components/main/widgets/PublicAftercareList'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '/public/after-care/calendar',
            name: Routes.PUBLIC_AFTERCARE_CALENDAR,
            component: () => import(/* webpackChunkName: "widgets" */ '@/components/main/widgets/PublicAftercareCalendar'),
            meta: {
                isPublic: true
            }
        },
        {
            path: '',
            component: () => import('@/components/main/Page'),
            pathMatch: 'full',
            redirect: store.getters.userRole === Roles.SCHOOL_INSTRUCTOR ? 'classes' : 'dashboard',
            children: [
                {
                    path: '/parent-portal',
                    name: Routes.PARENT_PORTAL,
                    component: () => import(/* webpackChunkName: "parent-portal" */ '@/components/main/parent-portal/ParentPortal'),
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'Parent Portal' }, { name: 'Preview' }]
                        },
                        resource: 'parent.portal'
                    }
                },
                {
                    path: '/dashboard',
                    name: Routes.DASHBOARD,
                    component: () => import(/* webpackChunkName: "dashboard" */ '@/components/main/dashboard/Dashboard'),
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'Dashboard' }]
                        },
                        resource: 'dashboard'
                    }
                },
                {
                    path: '/classes',
                    name: Routes.CLASS_PAGE,
                    component: () => import(/* webpackChunkName: "classes" */ '@/components/main/pages/classes/ClassPage'),
                    props: {
                        tab: 'Schedule'
                    },
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'My Classes' }]
                        },
                        resource: 'schedule.classes'
                    }
                },
                {
                    path: '/classes/form',
                    name: Routes.CLASS_FORM,
                    component: () => import(/* webpackChunkName: "classes"*/ '@/components/main/pages/class-form/ClassForm'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/classes'
                            },
                            items: [{ name: 'Add class', color: '#888b94' }]
                        },
                        resource: 'class.classes'
                    }
                },
                {
                    path: '/classes/form/:id',
                    name: Routes.CLASS_EDIT_FORM,
                    component: () => import(/* webpackChunkName: "classes"*/ '@/components/main/pages/class-form/ClassForm'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/classes'
                            },
                            items: [{ name: 'Edit class', color: '#888b94' }]
                        },
                        resource: 'class.classes'
                    }
                },
                {
                    path: '/classes/calendar',
                    name: Routes.CLASS_CALENDAR,
                    component: () => import(/* webpackChunkName: "classes" */ '@/components/main/pages/classes/ClassPage'),
                    props: {
                        tab: 'Calendar'
                    },
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'My Classes' }]
                        },
                        resource: 'calendar.classes'
                    }

                },
                {
                    path: '/classes/:id',
                    name: Routes.CLASS_ROSTERS,
                    component: () => import(/* webpackChunkName: "classes" */ '@/components/main/pages/classes/class-details/ClassDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/classes'
                            },
                            items: [{ name: 'My classes' }, { name: '$className' }]
                        },
                        resource: 'rosters.classes'
                    },
                    props: {
                        tab: 'Rosters'
                    }
                },
                {
                    path: '/classes/:id/wait-list',
                    name: Routes.CLASS_WAIT_LIST,
                    component: () => import(/* webpackChunkName: "classes" */ '@/components/main/pages/classes/class-details/ClassDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/classes'
                            },
                            items: [{ name: 'My classes' }, { name: '$className' }]
                        },
                        resource: 'waitlist.classes'
                    },
                    props: {
                        tab: 'Waitlist'
                    }
                },
                {
                    path: '/classes/:id/attendance',
                    name: Routes.CLASS_ATTENDANCE,
                    component: () => import(/* webpackChunkName: "classes" */ '@/components/main/pages/classes/class-details/ClassDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/classes'
                            },
                            items: [{ name: 'My classes' }, { name: '$className' }]
                        },
                        resource: 'attendance.classes'
                    },
                    props: {
                        tab: 'Attendance'
                    }
                },
                {
                    path: '/camps',
                    name: Routes.CAMP_PAGE,
                    component: () => import(/* webpackChunkName: "camps"*/ '@/components/main/pages/camps/CampPage'),
                    props: {
                        tab: 'Schedule'
                    },
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'My Camps' }]
                        },
                        resource: 'schedule.camps'
                    }
                },
                {
                    path: '/camps/form',
                    name: Routes.CAMP_FORM,
                    component: () => import(/* webpackChunkName: "camps"*/ '@/components/main/pages/camp-form/CampForm'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/camps'
                            },
                            items: [{ name: 'Add camp', color: '#888b94' }]
                        },
                        resource: 'camps.camps'
                    }
                },
                {
                    path: '/camps/form/:id',
                    name: Routes.CAMP_EDIT_FORM,
                    component: () => import(/* webpackChunkName: "camps"*/ '@/components/main/pages/camp-form/CampForm'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/camps'
                            },
                            items: [{ name: 'Edit camp', color: '#888b94' }]
                        },
                        resource: 'camps.camps'
                    }
                },
                {
                    path: '/camps/calendar',
                    name: Routes.CAMP_CALENDAR,
                    component: () => import(/* webpackChunkName: "camps"*/ '@/components/main/pages/camps/CampPage'),
                    props: {
                        tab: 'Calendar'
                    },
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'My Camps' }]
                        },
                        resource: 'calendar.camps'
                    }
                },
                {
                    path: '/camps/:id',
                    name: Routes.CAMP_ROSTERS,
                    component: () => import(/* webpackChunkName: "camps"*/ '@/components/main/pages/camps/camp-details/CampDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/camps'
                            },
                            items: [{ name: 'My camps' }, { name: '$campPricingName' }]
                        },
                        resource: 'rosters.camps'
                    },
                    props: {
                        tab: 'Rosters'
                    }
                },
                {
                    path: '/camps/:id/wait-list',
                    name: Routes.CAMP_WAIT_LIST,
                    component: () => import(/* webpackChunkName: "camps"*/ '@/components/main/pages/camps/camp-details/CampDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/camps'
                            },
                            items: [{ name: 'My camps' }, { name: '$campPricingName' }]
                        },
                        resource: 'waitlist.camps'
                    },
                    props: {
                        tab: 'Waitlist'
                    }
                },
                {
                    path: '/camps/:id/attendance',
                    name: Routes.CAMP_ATTENDANCE,
                    component: () => import(/* webpackChunkName: "camps"*/ '@/components/main/pages/camps/camp-details/CampDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/camps'
                            },
                            items: [{ name: 'My camps' }, { name: '$campPricingName' }]
                        },
                        resource: 'attendance.camps'
                    },
                    props: {
                        tab: 'Attendance'
                    }
                },
                {
                    path: '/after-care',
                    name: Routes.AFTERCARE_PAGE,
                    component: () => import(/* webpackChunkName: "aftercare" */ '@/components/main/pages/aftercare/AftercarePage'),
                    props: {
                        tab: 'Schedule'
                    },
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'After Care' }]
                        },
                        resource: 'schedule.aftercare'
                    }
                },
                {
                    path: '/after-care/calendar',
                    name: Routes.AFTERCARE_CALENDAR,
                    component: () => import(/* webpackChunkName: "aftercare" */ '@/components/main/pages/aftercare/AftercarePage'),
                    props: {
                        tab: 'Calendar'
                    },
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'After Care' }]
                        },
                        resource: 'calendar.aftercare'
                    }

                },
                {
                    path: '/after-care/form',
                    name: Routes.AFTERCARE_FORM,
                    component: () => import(/* webpackChunkName: "aftercare"*/ '@/components/main/pages/aftercare-form/AftercareForm'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/after-care'
                            },
                            items: [{ name: 'Add session', color: '#888b94' }]
                        },
                        resource: 'sessions.aftercare'
                    }
                },
                {
                    path: '/after-care/form/:id',
                    name: Routes.AFTERCARE_EDIT_FORM,
                    component: () => import(/* webpackChunkName: "aftercare"*/ '@/components/main/pages/aftercare-form/AftercareForm'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/after-care'
                            },
                            items: [{ name: 'Edit session', color: '#888b94' }]
                        },
                        resource: 'sessions.aftercare'
                    }
                },
                {
                    path: '/after-care/:id',
                    name: Routes.AFTERCARE_ROSTERS,
                    component: () => import(/* webpackChunkName: "aftercare"*/ '@/components/main/pages/aftercare/aftercare-details/AftercareDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/after-care'
                            },
                            items: [{ name: 'After Care' }, { name: '$aftercareSessionName' }]
                        },
                        resource: 'rosters.aftercare'
                    },
                    props: {
                        tab: 'Rosters'
                    }
                },
                {
                    path: '/after-care/:id/drop-in',
                    name: Routes.AFTERCARE_DROP_IN,
                    component: () => import(/* webpackChunkName: "aftercare"*/ '@/components/main/pages/aftercare/aftercare-details/AftercareDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/after-care'
                            },
                            items: [{ name: 'After Care' }, { name: '$aftercareSessionName' }]
                        },
                        resource: 'dropin.aftercare'
                    },
                    props: {
                        tab: 'Drop-in'
                    }
                },
                {
                    path: '/after-care/:id/wait-list',
                    name: Routes.AFTERCARE_WAIT_LIST,
                    component: () => import(/* webpackChunkName: "aftercare"*/ '@/components/main/pages/aftercare/aftercare-details/AftercareDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/after-care'
                            },
                            items: [{ name: 'After Care' }, { name: '$aftercareSessionName' }]
                        },
                        resource: 'waitlist.aftercare'
                    },
                    props: {
                        tab: 'Waitlist'
                    }
                },
                {
                    path: '/after-care/:id/attendance',
                    name: Routes.AFTERCARE_ATTENDANCE,
                    component: () => import(/* webpackChunkName: "aftercare"*/ '@/components/main/pages/aftercare/aftercare-details/AftercareDetailsPage'),
                    meta: {
                        breadcrumb: {
                            back: {
                                to: '/after-care'
                            },
                            items: [{ name: 'After Care' }, { name: '$aftercareSessionName' }]
                        },
                        resource: 'attendance.aftercare'
                    },
                    props: {
                        tab: 'Attendance'
                    }
                },
                {
                    path: '/transactions',
                    name: Routes.TRANSACTIONS,
                    props: {
                        tab: Routes.TRANSACTIONS
                    },
                    component: () => import(/* webpackChunkName: "transactions" */ '@/components/main/pages/transactions/TransactionsPage'),
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'Transactions' }]
                        },
                        resource: 'transactions'
                    }
                },
                {
                    path: '/transactions/invoices',
                    name: Routes.INVOICES,
                    props: {
                        tab: Routes.INVOICES
                    },
                    component: () => import(/* webpackChunkName: "transactions" */ '@/components/main/pages/transactions/TransactionsPage'),
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'Transactions' }]
                        },
                        resource: 'invoice'
                    }
                },
                {
                    path: '/transactions/coupons',
                    name: Routes.COUPONS,
                    props: {
                        tab: Routes.COUPONS
                    },
                    component: () => import(/* webpackChunkName: "transactions" */ '@/components/main/pages/transactions/TransactionsPage'),
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'Transactions' }]
                        },
                        resource: 'coupons'
                    }
                },
                {
                    path: '/contacts',
                    name: Routes.CONTACTS_PAGE,
                    component: () => import(/* webpackChunkName: "Contacts" */ '@/components/main/pages/contacts/Contacts'),
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'Contacts' }]
                        },
                        resource: 'contacts'
                    }
                },
                {
                    path: '/reports',
                    name: Routes.REPORTS_PAGE,
                    component: () => import(/* webpackChunkName: "reports" */ '@/components/main/pages/reports/Reports'),
                    meta: {
                        breadcrumb: {
                            items: [{ name: 'Reports' }]
                        },
                        resource: 'reports'
                    }
                },
                {
                    path: '/settings',
                    name: Routes.SETTINGS_LIST_PAGE,
                    component: () => import('@/components/main/settings/SettingsLayout'),
                    redirect: {
                        name: Routes.PERSONAL_INFO_PAGE
                    },
                    children: [
                        {
                            path: 'personal-info',
                            name: Routes.PERSONAL_INFO_PAGE,
                            component: () => import(/* webpackChunkName: "personal-info"*/ '@/components/main/settings/PersonalInfo'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Profile' }, { name: 'Personal Info' }]
                                },
                                resource: 'settings.profile.personal_info'
                            }
                        },
                        {
                            path: 'notifications',
                            name: Routes.NOTIFICATIONS_PAGE,
                            component: () => import(/* webpackChunkName: "notifications"*/ '@/components/main/settings/Notifications'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Profile' }, { name: 'Notifications' }]
                                },
                                resource: 'settings.profile.notifications'
                            }
                        },
                        {
                            path: 'widgets',
                            name: Routes.WIDGETS_PAGE,
                            component: () => import(/* webpackChunkName: "widgets"*/ '@/components/main/settings/Widgets'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Parent Portal' }, { name: 'Widgets' }]
                                },
                                resource: 'settings.parent_portal.widgets'
                            }
                        },
                        {
                            path: 'settings',
                            name: Routes.SETTINGS_PAGE,
                            component: () => import(/* webpackChunkName: "settings"*/ '@/components/main/settings/SettingsPage'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Parent Portal' }, { name: 'Settings' }]
                                },
                                resource: 'settings.parent_portal.settings'
                            }
                        },
                        {
                            path: 'teachers',
                            name: Routes.TEACHERS_PAGE,
                            component: () => import(/* webpackChunkName: "teachers"*/ '@/components/main/settings/teachers/Teachers'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'List' }, { name: 'Homeroom Teachers' }]
                                },
                                resource: 'settings.list.teachers'
                            }
                        },
                        {
                            path: 'pickup-options',
                            name: Routes.PICKUP_OPTIONS_PAGE,
                            component: () => import(/* webpackChunkName: "pickup-options"*/ '@/components/main/settings/pickup-options/PickupOptions'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'List' }, { name: 'Pickup Options' }]
                                },
                                resource: 'settings.list.pickup_options'
                            }
                        },
                        {
                            path: 't-shirt',
                            name: Routes.TSHIRTS_PAGE,
                            component: () => import(/* webpackChunkName: "t-shirts"*/ '@/components/main/settings/t-shirts/T-Shirts'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'List' }, { name: 'T-Shirt' }]
                                },
                                resource: 'settings.list.shirt'
                            }
                        },
                        {
                            path: 'locations',
                            name: Routes.LOCATIONS_PAGE,
                            component: () => import(/* webpackChunkName: "locations"*/ '@/components/main/settings/locations/Locations'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'List' }, { name: 'Locations' }]
                                },
                                resource: 'settings.list.locations'
                            }
                        },
                        {
                            path: 'custom-forms',
                            name: Routes.CUSTOM_FORMS_PAGE,
                            component: () => import(/* webpackChunkName: "custom-forms"*/ '@/components/main/settings/custom-forms/CustomForms'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'List' }, { name: 'Custom Forms' }]
                                },
                                resource: 'settings.list.custom_forms'
                            }
                        },
                         {
                            path: 'custom-forms/form',
                            name: Routes.CUSTOM_FORMS_CREATE_FORM,
                            component: () => import(/* webpackChunkName: "custom-forms"*/ '@/components/main/settings/custom-forms/create-form/AddCustomForm'),
                            meta: {
                                breadcrumb: {
                                    back: {
                                        to: '/settings/custom-forms'
                                    },
                                    items: [{ name: 'Add custom form', color: '#888b94' }]
                                },
                                resource: 'settings.list.custom_forms'
                            }
                        },
                        {
                            path: 'custom-forms/form/:id',
                            name: Routes.CUSTOM_FORMS_EDIT_FORM,
                            component: () => import(/* webpackChunkName: "custom-forms"*/ '@/components/main/settings/custom-forms/create-form/EditCustomForm'),
                            meta: {
                                breadcrumb: {
                                    back: {
                                        to: '/settings/custom-forms'
                                    },
                                    items: [{ name: 'Edit custom form', color: '#888b94' }]
                                },
                                resource: 'settings.list.custom_forms'
                            }
                        },
                        {
                            path: 'profile',
                            name: Routes.PROFILE_PAGE,
                            component: () => import(/* webpackChunkName: "profile"*/ '@/components/main/settings/Profile'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Organizations' }, { name: 'Profile' }]
                                },
                                resource: 'settings.organization.profile'
                            }
                        },
                        {
                            path: 'policies',
                            name: Routes.POLICIES_PAGE,
                            component: () => import(/* webpackChunkName: "policies"*/ '@/components/main/settings/policies/Policies'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Organizations' }, { name: 'Policies' }]
                                },
                                resource: 'settings.organization.policies'
                            }
                        },
                        {
                            path: 'billing',
                            name: Routes.BILLING_PAGE,
                            component: () => import(/* webpackChunkName: "billing"*/ '@/components/main/settings/billing/Billing'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Organizations' }, { name: 'Billing' }]
                                },
                                resource: 'settings.organization.billing'
                            }
                        },
                        {
                            path: 'users',
                            name: Routes.USERS_PAGE,
                            component: () => import(/* webpackChunkName: "users"*/ '@/components/main/settings/users/Users'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Organizations' }, { name: 'Users' }]
                                },
                                resource: 'settings.organization.users'
                            }
                        },
                        {
                            path: 'sms_history',
                            name: Routes.SMS_HISTORY_PAGE,
                            component: () => import(/* webpackChunkName: "sms-history"*/ '@/components/main/settings/sms-history/SMS'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Communication' }, { name: 'SMS History' }]
                                },
                                resource: 'settings.communication.sms_history'
                            }
                        },
                        {
                            path: 'email_history',
                            name: Routes.EMAIL_HISTORY_PAGE,
                            component: () => import(/* webpackChunkName: "email-history"*/ '@/components/main/settings/email-history/Emails'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Communication' }, { name: 'Email History' }]
                                },
                                resource: 'settings.communication.email_history'
                            }
                        },
                        {
                            path: 'apps',
                            name: Routes.APPS_PAGE,
                            component: () => import(/* webpackChunkName: "apps"*/ '@/components/main/settings/Apps'),
                            meta: {
                                breadcrumb: {
                                    items: [{ name: 'Integrations' }, { name: 'Apps' }]
                                },
                                resource: 'settings.integrations.apps'
                            }
                        }
                    ]
                },
            ]
        },
        {
            path: '*',
            name: Routes.PAGE404,
            component: () => import('@/components/no-auth/Page404'),
            meta: {
                isPublic: true
            }
        }
    ]
});

router.beforeEach((to, from, next) => {
    const { userRole = null } = UserData.getUserDataFromLocalStorage() || {};

    if (!userRole && to.matched.some(record => record.meta.isPublic)) {
        return next();
    }

    cancelToken('Request cancelled.');

    resetPage();

    if (userRole) {
        definePermissionBuilder();

        redirectFromLogin();

        checkRequiredAuth();
    } else {
        redirectToLogin();
    }

    function redirectToLogin() {
        store.commit(AccessListMutations.SET_ABILITY, null);

        next({
            name: Routes.LOGIN
        });
    }

    function redirectFromLogin() {
        if (_.some(['/login', '/sign-up'], route => route === to.path)) {
            const { adminViewToken } = to.query;

            adminViewToken ? next() : navigateToStartPage(userRole);
        } else {
            next();
        }
    }

    function navigateToStartPage() {
        const startPage = getStartPage(userRole);
        next({ name: startPage });
    }

    function checkRequiredAuth() {
        if (to.matched.some(record => !record.meta.isPublic)) {
            // check particular routes on exist permission restrictions
            store.watch(() => store.getters.ability, (ability) => {
                !_.isNull(ability) && checkAccessToRouteByPermissions();
            }, { immediate: true });
        }
    }

    function checkAccessToRouteByPermissions() {
        const canNavigate = to.meta.resource
            ? store.getters.ability.can(to.meta.action || 'view', to.meta.resource)
            : true;

        return !canNavigate
            ? navigateToStartPage()
            : next();
    }

    function definePermissionBuilder() {
        _.isNull(store.getters.ability) && store.dispatch('configureAbility', userRole);
    }
});

router.afterEach(RecaptchaVisibilityManager);

export default router;


// WEBPACK FOOTER //
// ./src/router/index.js